import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { operationType, selectDataType } from "../../../../../services/types";
import { updateNodeValue } from "../../../../../services/method";
import axios from "axios";
import { TextInput } from "../../../../form/textInput";
import { Textarea } from "../../../../form/textarea";
import { Select } from "../../../../form/select";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { ExpandedBlock } from "../../../expanded";
import { webURL } from "../../../../../services/API";
import { AuthenticateSelect } from "../../../../auth/select";
import { Main, Styled } from "./style";

type TableRow = { name: string; type?: string };

type SqlProps = NodeProps<operationType> & {
  setChildMinimize?: (fn: (id: string) => void) => void;
  setChildSettings?: (fn: (id: string) => void) => void;
};

export function Sql({
  id,
  data,
  setChildMinimize,
  setChildSettings,
}: SqlProps) {
  const { setNodes } = useReactFlow();
  const [operations, setOperations] = React.useState<selectDataType[]>([
    { id: 1, name: "Execute Query", refId: "executeQuery", isActive: true },
    { id: 2, name: "Select", refId: "select" },
    { id: 3, name: "Insert", refId: "insert" },
    { id: 4, name: "Update", refId: "update" },
    { id: 5, name: "Delete", refId: "delete" },
    { id: 6, name: "Upsert", refId: "upsert" },
    // { id: 7, name: "Stored Procedure", refId: "executeStoredProcedure" },
    { id: 7, name: "Let AI Agent Decide", refId: "agent_decide" },
  ]);
  const [settings, setSettings] = React.useState<settingsDataType>({
    isToggle: false,
  });
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isFetchingTables, setIsFetchingTables] = React.useState(false);
  const [tablesError, setTablesError] = React.useState<string>("");
  const [tableOptions, setTableOptions] = React.useState<selectDataType[]>([]);

  React.useEffect(() => {
    if (setChildMinimize)
      setChildMinimize(() => {
        setIsExpanded((prev) => !prev);
      });
    if (setChildSettings)
      setChildSettings(() => {
        setSettings((prev) => ({ ...prev, isToggle: !prev.isToggle }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setChildMinimize, setChildSettings]);

  React.useEffect(() => {
    if (!data.value) {
      updateNodeValue(id, setNodes, {
        operation: operations[0].refId,
        //   port: 3306,
        sslMode: "preferred",
      });
    } else {
      setOperations((ops) =>
        ops.map((op) => ({
          ...op,
          isActive: op.refId === data.value.operation,
        }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectOperation = (selectId: number) => {
    setOperations((ops) =>
      ops.map((op) => ({ ...op, isActive: op.id === selectId }))
    );
    updateNodeValue(id, setNodes, {
      operation: operations.find((op) => op.id === selectId)?.refId,
    });
  };

  const handleAuthenticate = (type: "add" | "remove", authData?: any) => {
    if (type === "add" && authData) {
      const sslConfigRaw =
        typeof authData.sslConfig === "string"
          ? authData.sslConfig
          : typeof authData.ssl === "object"
          ? JSON.stringify(authData.ssl, null, 2)
          : data.value?.sslConfig;

      updateNodeValue(id, setNodes, {
        connection: authData,
        host: authData.host ?? data.value?.host,
        port: authData.port ?? data.value?.port,
        user: authData.user ?? data.value?.user,
        password: authData.password ?? data.value?.password,
        database: authData.database ?? data.value?.database,
        ssl: authData.ssl ?? data.value?.ssl,
        sslMode: authData.sslMode ?? data.value?.sslMode ?? "preferred",
        caCert: authData.caCert ?? data.value?.caCert,
        sslRejectUnauthorized:
          authData.sslRejectUnauthorized ?? data.value?.sslRejectUnauthorized,
        useMariaDb: authData.useMariaDb ?? data.value?.useMariaDb,
        sslConfig: sslConfigRaw,
      });
    } else if (type === "remove") {
      updateNodeValue(id, setNodes, { connection: null });
    }
  };

  const handleChangeInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    keyRef?: string
  ) => {
    if (!keyRef) return;
    const rawValue = e.target.value;
    const value =
      e.target.type === "number"
        ? rawValue === ""
          ? ""
          : Number(rawValue)
        : rawValue;
    updateNodeValue(id, setNodes, { [keyRef]: value });
  };

  const handleJsonChange = (
    rawKey: string,
    targetKey: string,
    rawValue: string
  ) => {
    let parsed: any = rawValue;
    try {
      parsed = rawValue ? JSON.parse(rawValue) : undefined;
    } catch {
      parsed = rawValue;
    }
    updateNodeValue(id, setNodes, { [rawKey]: rawValue, [targetKey]: parsed });
  };

  const handleConflictKeysChange = (value: string) => {
    const keys = value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
    updateNodeValue(id, setNodes, {
      conflictKeysRaw: value,
      conflictKeys: keys,
    });
  };

  const HIDDEN_TABLE = "table_events";

  React.useEffect(() => {
    const tables = Array.isArray(data.value?.tables) ? data.value.tables : [];
    const filteredTables = tables.filter(
      (table: TableRow) =>
        (table?.name || "").toLowerCase() !== HIDDEN_TABLE &&
        (table?.type || "").toLowerCase() !== HIDDEN_TABLE
    );
    const options = filteredTables.map((table: TableRow, index: number) => ({
      id: index + 1,
      name: table.name || `Table ${index + 1}`,
      refId: table.name || `table_${index + 1}`,
      isActive: (table.name || `table_${index + 1}`) === data.value?.table,
    }));
    setTableOptions(options);
  }, [data.value?.tables, data.value?.table]);

  const buildTablePayload = () => {

    const connection = data.value?.connection as any;
    const savedCreds =
      connection?.sql || connection?.usedCredentials || connection || {};

    const host = data.value?.host || savedCreds.host;
    const user = data.value?.user || savedCreds.user;
    const password = data.value?.password || savedCreds.password;
    const database = data.value?.database || savedCreds.database;
    const type = connection?.sql?.type;
    const port = data.value?.port || savedCreds?.port;
    return {
      host,
      user,
      password,
      database,
      type,
      port,
      ssl: savedCreds.ssl ?? data.value?.ssl ?? false,
      sslMode: savedCreds.sslMode ?? data.value?.sslMode ?? "preferred",
      caCert: savedCreds.caCert ?? data.value?.caCert,
      sslConfig:
        savedCreds.sslConfig ??
        data.value?.sslConfig ??
        savedCreds.ssl ??
        undefined,
      rejectUnauthorized:
        savedCreds.rejectUnauthorized ??
        data.value?.sslRejectUnauthorized ??
        false,
      useMariaDb: savedCreds.useMariaDb ?? data.value?.useMariaDb ?? false,
    };
  };

  const tablePayload = buildTablePayload();

  const handleSelectTable = async (selectId?: number) => {
    if (
      !tablePayload.host ||
      !tablePayload.user ||
      !tablePayload.password ||
      !tablePayload.database
    ) {
      setTablesError("Missing required credentials to fetch tables.");
      return;
    }

    setIsFetchingTables(true);
    setTablesError("");
    try {
      const response = await axios.post(`${webURL}/sql/tables`, tablePayload);
      const tables: TableRow[] = response?.data?.tables || [];
      const filteredTables = tables.filter(
        (table) => (table?.name || "").toLowerCase() !== HIDDEN_TABLE
      );
      const selectedTable =
        typeof selectId === "number"
          ? filteredTables[selectId - 1]?.name || filteredTables[selectId]?.name
          : undefined;

      updateNodeValue(id, setNodes, {
        tables: filteredTables,
        table: selectedTable || data.value?.table || filteredTables[0]?.name,
      });
    } catch (error: any) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        "Failed to fetch tables";
      setTablesError(message);
    } finally {
      setIsFetchingTables(false);
    }
  };

  // const HIDDEN_TABLE = "table_events";

  React.useEffect(() => {
    setTableOptions((ops) => {
      const filtered = ops.filter(
        (op) => (op.refId || op.name || "").toLowerCase() !== HIDDEN_TABLE
      );
      return filtered.length === ops.length ? ops : filtered;
    });
  }, [data.value?.tables]);

  const handleSelectTableOption = (selectId: number) => {
    setTableOptions((ops) => {
      const filtered = ops.filter(
        (op) => (op.refId || op.name || "").toLowerCase() !== HIDDEN_TABLE
      );
      const updated = filtered.map((op) => ({
        ...op,
        isActive: op.id === selectId,
      }));
      const refId = updated.find((op) => op.id === selectId)?.refId;
      if (refId && refId.toLowerCase() !== HIDDEN_TABLE)
        updateNodeValue(id, setNodes, { table: refId });
      return updated;
    });
  };

  const isWarning =
    !tablePayload.host ||
    !tablePayload.user ||
    !tablePayload.password ||
    !tablePayload.database;

  // const valuesRaw =
  //   data.value?.valuesRaw ??
  //   (Array.isArray(data.value?.values)
  //     ? JSON.stringify(data.value.values)
  //     : "");
  const dataRaw =
    data.value?.dataRaw ??
    (data.value?.data && typeof data.value.data === "object"
      ? JSON.stringify(data.value.data, null, 2)
      : "");

  return (
    <>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />

      <Main>
        {/* Status Icons */}
        { isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={ 21 } /></span>
          </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={ 21 } /></span>
          </> : null }

        {/* Expanded Block */}
        {isExpanded && (
          <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            style={{ minWidth: "430px" }}>
            <Styled>
              {/* Authentication Section */}
                <div className="authWrapper">
                  <AuthenticateSelect
                    type="sql"
                    defaultId={data.value?.connection?.id}
                    handleAuthenticate={handleAuthenticate}
                    background={data.background}
                  />
              </div>
              <div className="actions">
                <button
                  className="btn1 withBorder"
                  disabled={
                    isFetchingTables ||
                    !tablePayload.host ||
                    !tablePayload.user ||
                    !tablePayload.password ||
                    !tablePayload.database
                  }
                  onClick={() => handleSelectTable()}>
                  {isFetchingTables ? "Fetching..." : "Fetch Tables"} in {tablePayload?.type || "mysql"}
                </button>
                {tablesError ? (
                  <span className="status error">{tablesError}</span>
                ) : null}
              </div>

              {/* Operation Section */}
              <div className="section">
                <Select
                  title="Operation"
                  data={operations}
                  handleSelectItem={handleSelectOperation}
                  isSmall={true}
                />

                <div className="operationFields">
                  {/* SQL / Query Textarea */}
                  {["executeQuery", "agent_decide"].includes(
                    data.value?.operation || ""
                  ) && (
                    <Textarea
                      keyRef="sql"
                      value={data.value?.sql}
                      placeholder="SELECT * FROM users WHERE status = 'active';"
                      handleChange={(e) =>
                        updateNodeValue(id, setNodes, { sql: e.target.value })
                      }
                    />
                  )}
                  {/* Values (JSON array) */}
                  {/* {[
                    "executeQuery",
                    "agent_decide",
                    "executeStoredProcedure",
                    "update",
                    "upsert",
                  ].includes(data.value?.operation || "") && (
                    <div className="mt-4 mb-4">
                      <Textarea
                        title="Values (JSON array)"
                        value={valuesRaw}
                        placeholder='["param1", 10]'
                        handleChange={(e) =>
                          handleJsonChange(
                            "valuesRaw",
                            "values",
                            e.target.value
                          )
                        }
                      />
                    </div>
                  )} */}
                  {/* Table & Data */}
                  {["insert", "update", "delete", "upsert", "select"].includes(
                    data.value?.operation || ""
                  ) && (
                    <>
                      {tableOptions.length ? (
                        <div className="mb-2 ">
                          <Select
                            title="Table"
                            data={tableOptions}
                            handleSelectItem={handleSelectTableOption}
                            isSmall={true}
                          />
                        </div>
                      ) : (
                        <TextInput
                          keyRef="table"
                          title="Table"
                          placeholder="users"
                          value={data.value?.table}
                          handleChange={handleChangeInput}
                        />
                      )}
                      {["insert", "update", "upsert"].includes(
                        data.value?.operation || ""
                      ) && (
                        <Textarea
                          value={dataRaw}
                          placeholder='{"name": "John", "email": "john@doe.com"}'
                          handleChange={(e) =>
                            handleJsonChange("dataRaw", "data", e.target.value)
                          }
                        />
                      )}
                    </>
                  )}
                  {/* Columns for SELECT */}
                  {data.value?.operation === "select" && (
                    <TextInput
                      keyRef="columns"
                      title="Columns"
                      placeholder="* or id, name, email"
                      value={data.value?.columns || ""}
                      handleChange={handleChangeInput}
                    />
                  )}
                  {/* Where Clause */}
                  {["update", "select", "delete"].includes(
                    data.value?.operation || ""
                  ) && (
                    <TextInput
                      keyRef="where"
                      title="Where"
                      placeholder="status = ?"
                      value={data.value?.where}
                      handleChange={handleChangeInput}
                    />
                  )}
                  {/* Conflict Keys */}
                  {data.value?.operation === "upsert" && (
                    <TextInput
                      keyRef="conflictKeysRaw"
                      title="Conflict"
                      placeholder="id, email"
                      value={data.value?.conflictKeysRaw}
                      handleChange={(e) =>
                        handleConflictKeysChange(e.target.value)
                      }
                    />
                  )}
                  {/* Stored Procedure */}
                 
                  {/* {data.value?.operation === "executeStoredProcedure" && (
                    <TextInput
                      keyRef="procedure"
                      title="Procedure"
                      placeholder="sp_update_user"
                      value={data.value?.procedure}
                      handleChange={handleChangeInput}
                    />
                  )} */}
                </div>
              </div>
            </Styled>
          </ExpandedBlock>
        )}
      </Main>
    </>
  );
}